/**
 * ShortIntelligencePanel — short-side advisory surface.
 *
 * Backend: /api/v1/short-intelligence/* (10 advisory-only routes).
 * The engine guarantees `short_execution_disabled: true` on every row. The
 * panel re-asserts this in a visible badge and renders a loud
 * SqueezeWarningBadge whenever the engine returns a `squeeze_warning` string.
 *
 * Lanes shown (per operator brief):
 *   - Top Short Candidates       (top-shorts)
 *   - Institutional Exits        (institutional-exits)
 *   - AI Hype Risk               (ai-hype-risk)
 *   - Earnings Disaster Watch    (earnings-disasters)
 *   - Short Squeeze Watch        (squeeze-risk)
 *
 * Scores shown: short conviction, short squeeze risk (0–100).
 * Naming rule: this surface is "Short Intelligence" — the engine and UI must
 * never present themselves as auto-executing shorts (advisory-only is the law).
 *
 * Marker strings: "short-intelligence", "phase-short-intel-ui-v1".
 */
import React, { useEffect, useState } from "react";
import SymbolChartButton from './SymbolChartButton';  // ADV-P0K
import {
  AdvisoryOnlyBadge,
  DataQualityChip,
  ScoreBar,
  ScoreChip,
  SqueezeWarningBadge,
  formatGeneratedAtET,
  formatLossPct,
  humanize,
} from "./ScoreBar";
import {
  getTopShorts,
  getInstitutionalExits,
  getAIHypeRisk,
  getEarningsDisasters,
  getSqueezeRisk,
  getShortIntelligenceLatest,
  type ShortCandidate,
  type ShortIntelligencePayload,
} from "../lib/shortIntelligenceClient";

type LaneKey = "top" | "exits" | "hype" | "earnings" | "squeeze";

interface LaneDef {
  key: LaneKey;
  label: string;
  description: string;
  testid: string;
  emptyHint: string;
  highlightSqueeze: boolean;
}

const LANES: LaneDef[] = [
  {
    key: "top",
    label: "Top Short Candidates",
    description:
      "Names with the highest short-conviction composite. Listed for awareness — Adversyn never enables short execution from this surface.",
    testid: "short-intel-lane-top",
    emptyHint: "No high-conviction short candidates on the latest run.",
    highlightSqueeze: true,
  },
  {
    key: "exits",
    label: "Institutional Exits",
    description:
      "Heavy distribution patterns suggesting institutional unwind. High conviction here usually means follow-through, but verify with primary sources.",
    testid: "short-intel-lane-exits",
    emptyHint: "No institutional-exit patterns flagged.",
    highlightSqueeze: false,
  },
  {
    key: "hype",
    label: "AI Hype Risk",
    description:
      "Names where the AI-narrative premium looks stretched relative to fundamentals. Watch for narrative break.",
    testid: "short-intel-lane-hype",
    emptyHint: "No AI-hype-risk names surfaced.",
    highlightSqueeze: false,
  },
  {
    key: "earnings",
    label: "Earnings Disaster Watch",
    description:
      "Post-print breakdowns where guidance, EPS, or revenue miss is driving structural weakness.",
    testid: "short-intel-lane-earnings",
    emptyHint: "No earnings-disaster names this run.",
    highlightSqueeze: false,
  },
  {
    key: "squeeze",
    label: "Short Squeeze Watch",
    description:
      "Names with elevated short interest and meme/retail signal. These are the ones to NOT short — squeeze risk dominates.",
    testid: "short-intel-lane-squeeze",
    emptyHint: "No active squeeze-risk names.",
    highlightSqueeze: true,
  },
];

interface LaneState {
  loading: boolean;
  payload: ShortIntelligencePayload | null;
  error: string | null;
}

const EMPTY_LANE: LaneState = { loading: true, payload: null, error: null };

function CandidateCard({
  c,
  highlightSqueeze,
}: {
  c: ShortCandidate;
  highlightSqueeze: boolean;
}) {
  const ticker = c.ticker || "(unknown)";
  return (
    <li
      data-testid="short-intel-candidate-card"
      className="rounded-lg border border-slate-700 bg-slate-900/60 p-3 space-y-2"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-base font-black tracking-tight text-white">{ticker}</span>
            <SymbolChartButton symbol={c.ticker} />
            {c.company_name ? (
              <span className="text-xs text-slate-400 truncate">{c.company_name}</span>
            ) : null}
          </div>
          <div className="text-[11px] text-slate-400 mt-0.5">
            {humanize(c.short_type)}
            {c.sector ? <span className="text-slate-500"> · {c.sector}</span> : null}
          </div>
        </div>
        <div className="text-right">
          <div className="text-sm font-bold text-rose-300">{formatLossPct(c.current_loss_pct)}</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Session</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5">
        <ScoreChip label="Conviction" value={c.short_conviction_score} tone="bearish" />
        <ScoreChip label="Squeeze Risk" value={c.short_squeeze_risk_score} tone="warning" />
        <DataQualityChip quality={c.data_quality} missing={c.missing_inputs} />
      </div>

      {highlightSqueeze ? <SqueezeWarningBadge message={c.squeeze_warning} /> : null}

      {c.short_thesis ? (
        <p className="text-xs text-slate-300 leading-snug">{c.short_thesis}</p>
      ) : null}
      {c.beginner_explanation ? (
        <p className="text-[11px] text-slate-400 leading-snug italic">{c.beginner_explanation}</p>
      ) : null}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px]">
        <div className="rounded border border-slate-800 px-2 py-1.5">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Best action</div>
          <div className="text-slate-200">{humanize(c.best_action)}</div>
        </div>
        <div className="rounded border border-slate-800 px-2 py-1.5">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Invalidation</div>
          <div className="text-slate-200">{humanize(c.invalidation_level)}</div>
        </div>
      </div>
    </li>
  );
}

function LaneSection({
  lane,
  state,
}: {
  lane: LaneDef;
  state: LaneState;
}) {
  const rows = state.payload?.candidates || [];
  return (
    <section
      data-testid={lane.testid}
      className="rounded-lg border border-slate-700/60 bg-slate-900/40 p-4 space-y-3"
    >
      <header className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-bold uppercase tracking-widest text-rose-200">
            {lane.label}
          </h3>
          <p className="text-xs text-slate-400 mt-1 leading-snug">{lane.description}</p>
        </div>
        <span className="text-[10px] uppercase tracking-widest text-slate-500">
          {state.loading ? "Loading…" : `${rows.length} on watch`}
        </span>
      </header>

      {state.error ? (
        <div className="rounded border border-rose-500/30 bg-rose-500/5 px-3 py-2 text-xs text-rose-200">
          Engine read failed: {state.error}. Surface stays advisory-only.
        </div>
      ) : state.loading ? (
        <div className="text-xs text-slate-500">Loading lane…</div>
      ) : rows.length === 0 ? (
        <div className="rounded border border-dashed border-slate-700 px-3 py-4 text-center text-xs text-slate-500">
          {lane.emptyHint}
        </div>
      ) : (
        <ul className="space-y-2">
          {rows.slice(0, 6).map((c, i) => (
            <CandidateCard
              key={`${c.ticker || "x"}-${i}`}
              c={c}
              highlightSqueeze={lane.highlightSqueeze}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

export default function ShortIntelligencePanel() {
  const [latest, setLatest] = useState<LaneState>(EMPTY_LANE);
  const [top, setTop] = useState<LaneState>(EMPTY_LANE);
  const [exits, setExits] = useState<LaneState>(EMPTY_LANE);
  const [hype, setHype] = useState<LaneState>(EMPTY_LANE);
  const [earnings, setEarnings] = useState<LaneState>(EMPTY_LANE);
  const [squeeze, setSqueeze] = useState<LaneState>(EMPTY_LANE);

  const load = async () => {
    setLatest((s) => ({ ...s, loading: true, error: null }));
    setTop((s) => ({ ...s, loading: true, error: null }));
    setExits((s) => ({ ...s, loading: true, error: null }));
    setHype((s) => ({ ...s, loading: true, error: null }));
    setEarnings((s) => ({ ...s, loading: true, error: null }));
    setSqueeze((s) => ({ ...s, loading: true, error: null }));
    const [la, t, e, h, ea, sq] = await Promise.all([
      getShortIntelligenceLatest(),
      getTopShorts(),
      getInstitutionalExits(),
      getAIHypeRisk(),
      getEarningsDisasters(),
      getSqueezeRisk(),
    ]);
    const mk = (env: { ok: boolean; data: ShortIntelligencePayload | null; error?: { message: string } | null }): LaneState => ({
      loading: false,
      payload: env.ok ? env.data : null,
      error: env.ok ? null : env.error?.message || "fetch failed",
    });
    setLatest(mk(la));
    setTop(mk(t));
    setExits(mk(e));
    setHype(mk(h));
    setEarnings(mk(ea));
    setSqueeze(mk(sq));
  };

  useEffect(() => {
    load();
    const id = window.setInterval(load, 120_000);
    return () => window.clearInterval(id);
  }, []);

  const generatedAtET =
    latest.payload?.generated_at_et ||
    formatGeneratedAtET(latest.payload?.generated_at);

  const firstCand = latest.payload?.candidates?.[0];

  return (
    <div
      data-testid="short-intelligence-panel"
      className="rounded-xl border border-rose-500/20 bg-slate-950/60 p-4 space-y-4"
    >
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-base md:text-lg font-black uppercase italic tracking-tight text-rose-200">
            Short Intelligence
          </h2>
          <p className="text-xs text-slate-400 mt-1 max-w-2xl leading-snug">
            Research on names where the engine sees short-side conviction —
            paired with squeeze-risk warnings on names you should NOT short. This
            surface never enables short execution. Direct trade instructions
            belong in the Decision Layer.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <AdvisoryOnlyBadge />
          <span className="text-[10px] uppercase tracking-widest text-slate-500">
            Updated: {generatedAtET}
          </span>
        </div>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <ScoreBar
          label="Short conviction (top candidate)"
          value={firstCand?.short_conviction_score ?? null}
          tone="bearish"
          hint="Composite of breakdown, catalyst, institutional exit, continuation."
        />
        <ScoreBar
          label="Squeeze risk (top candidate)"
          value={firstCand?.short_squeeze_risk_score ?? null}
          tone="warning"
          hint="Higher = avoid shorting; squeeze potential dominates."
        />
      </div>

      <SqueezeWarningBadge message={firstCand?.squeeze_warning} />

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {LANES.map((lane) => {
          const state =
            lane.key === "top"
              ? top
              : lane.key === "exits"
              ? exits
              : lane.key === "hype"
              ? hype
              : lane.key === "earnings"
              ? earnings
              : squeeze;
          return <LaneSection key={lane.key} lane={lane} state={state} />;
        })}
      </div>

      <p className="text-[11px] text-slate-500 leading-snug">
        Short Intelligence is research, not orders. Adversyn never automatically
        opens short positions. Squeeze warnings are loud on purpose — they mark
        names where shorting is structurally unsafe even when conviction looks
        high.
      </p>
    </div>
  );
}
